import { useEffect, useRef } from 'react';

export interface UnsavedMessages {
  readonly heading: string;
  readonly explanation: string;
  readonly save: string;
  readonly discard: string;
  readonly keep: string;
}

export const EN_UNSAVED: UnsavedMessages = {
  heading: 'Keep your changes?',
  explanation:
    'You have an unfinished event. Save it to your event list before moving on, or discard your changes.',
  save: 'Save and continue',
  discard: 'Discard changes',
  keep: 'Keep editing',
};

/** A native modal supplies focus containment and makes the page behind it inert. */
export function UnsavedDialog({
  m,
  onSave,
  onDiscard,
  onKeep,
}: {
  readonly m: UnsavedMessages;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
  readonly onKeep: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    element?.showModal();
    keep.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="unsaved-dialog"
      aria-labelledby="unsaved-heading"
      aria-describedby="unsaved-explanation"
      onCancel={(event) => {
        event.preventDefault();
        onKeep();
      }}
    >
      <h2 id="unsaved-heading">{m.heading}</h2>
      <p id="unsaved-explanation">{m.explanation}</p>
      <div className="unsaved-actions">
        <button type="button" className="funnel-go" onClick={onSave}>
          {m.save}
        </button>
        <button type="button" onClick={onDiscard}>
          {m.discard}
        </button>
        <button ref={keep} type="button" onClick={onKeep}>
          {m.keep}
        </button>
      </div>
    </dialog>
  );
}
